import { useState, useEffect } from 'react';
import { db, storage } from '@/lib/firebase';
import { useTranslation } from 'react-i18next';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { 
  Save, 
  Loader2, 
  Globe,
  Image as ImageIcon,
  Upload,
  Trash2
} from 'lucide-react';

export default function LandingImagesPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const [uploadStates, setUploadStates] = useState<Record<string, boolean>>({});
  
  const [images, setImages] = useState({
    heroImage: '',
    aboutImage: '',
    aboutHeroImage: '',
    aboutStoryImage: '',
    aboutWhy1Image: '',
    aboutWhy2Image: '',
    aboutWhy3Image: '',
    aboutWhy4Image: '',
    aboutCTAImage: '',
    menuHeroImage: '',
    galleryHeroImage: '',
    galleryCTAImage: '',
    locationHeroImage: '',
    locationServiceAreaImage: '',
  });

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'landing_images'), (docSnap) => {
      if (docSnap.exists()) {
        setImages((prev) => ({ ...prev, ...docSnap.data() }));
      }
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);

    try {
      await setDoc(doc(db, 'settings', 'landing_images'), {
        ...images,
        updatedAt: new Date().toISOString()
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving landing images:", error);
      alert("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadStates(prev => ({ ...prev, [field]: true }));

    try {
      const oldUrl = (images as any)[field];
      const fileRef = ref(storage, `landing/${field}_${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(fileRef, file);

      uploadTask.on('state_changed', 
        (snapshot) => {
          // can track progress here
        }, 
        (error) => {
          console.error("Upload error:", error);
          alert("Error uploading image");
          setUploadStates(prev => ({ ...prev, [field]: false }));
        }, 
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          if (oldUrl && oldUrl.includes('firebasestorage')) {
            try {
              await deleteObject(ref(storage, oldUrl));
            } catch (err) {
              console.error("Failed to delete old image:", err);
            }
          }

          setImages(prev => ({ ...prev, [field]: downloadURL }));
          setUploadStates(prev => ({ ...prev, [field]: false }));
        }
      );
    } catch (error) {
      console.error("Upload process error:", error);
      setUploadStates(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleDeleteImage = async (field: string) => {
    const currentUrl = (images as any)[field];
    if (window.confirm(t('admin_landing_delete_confirm', 'Are you sure you want to delete this image?'))) {
      if (currentUrl && currentUrl.includes('firebasestorage')) {
        try {
          await deleteObject(ref(storage, currentUrl));
        } catch (error) {
          console.error("Error deleting image from storage:", error);
        }
      }
      
      const newImages = { ...images, [field]: '' };
      setImages(newImages);
      
      try {
        await setDoc(doc(db, 'settings', 'landing_images'), {
          ...newImages,
          updatedAt: new Date().toISOString()
        });
      } catch (error) {
        console.error("Error auto-saving after deletion:", error);
      }
    }
  };

  const renderImageCard = (field: string, title: string) => {
    const isUploading = uploadStates[field] || false;
    const value = (images as any)[field] || '';

    return (
      <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3 text-white">
            <ImageIcon size={24} className="text-[#F57F25]" />
            <h3 className="font-black uppercase tracking-widest text-xs">{title}</h3>
          </div>
          <div className="flex items-center gap-3">
            {value && (
              <button 
                type="button"
                onClick={() => handleDeleteImage(field)}
                className="px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-colors flex items-center gap-2"
                disabled={isUploading}
              >
                <Trash2 size={16} /> Delete
              </button>
            )}
            <label className={`cursor-pointer px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-colors flex items-center gap-2 ${isUploading ? 'bg-white/5 text-white/50 cursor-not-allowed' : 'bg-[#F57F25]/20 hover:bg-[#F57F25]/30 text-[#F57F25]'}`}>
              {isUploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {isUploading ? 'Uploading...' : 'Upload'}
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={(e) => handleImageUpload(e, field)}
                disabled={isUploading}
              />
            </label>
          </div>
        </div>
        {value ? (
          <div className="mt-4 rounded-xl overflow-hidden border border-white/10 h-64 relative group">
            <img src={value} alt={`${title} Preview`} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="mt-4 rounded-xl border-2 border-dashed border-white/10 h-64 flex flex-col items-center justify-center text-white/20 gap-3">
            <ImageIcon size={32} className="opacity-50" />
            <span className="text-xs font-bold uppercase tracking-widest">No image uploaded</span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#F57F25]" size={48} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-10">
      <div>
        <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('admin_landing_images_title', 'Landing Page Images')}</h1>
        <p className="text-white/50 text-sm">{t('admin_landing_images_sub', 'Manage static images displayed on the home and about pages')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-10">
        
        {/* Home Page Images Section */}
        <section className="space-y-6">
          <div className="flex items-center gap-3 border-b border-white/10 pb-4">
            <h2 className="text-xl font-black text-white uppercase tracking-tight">Home Page</h2>
          </div>
          <div className="grid grid-cols-1 gap-8">
            {renderImageCard('heroImage', t('admin_landing_hero', 'Hero Image URL'))}
            {renderImageCard('aboutImage', t('admin_landing_about', 'About Us Section Image URL (Home Page)'))}
          </div>
        </section>

        {/* Menu Page Images Section */}
        <section className="space-y-6 pt-4">
          <div className="flex items-center gap-3 border-b border-white/10 pb-4">
            <h2 className="text-xl font-black text-white uppercase tracking-tight">Menu Page</h2>
          </div>
          <div className="grid grid-cols-1 gap-8">
            {renderImageCard('menuHeroImage', 'Menu Hero Image')}
          </div>
        </section>

        {/* Gallery Page Images Section */}
        <section className="space-y-6 pt-4">
          <div className="flex items-center gap-3 border-b border-white/10 pb-4">
            <h2 className="text-xl font-black text-white uppercase tracking-tight">Gallery Page</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {renderImageCard('galleryHeroImage', 'Gallery Hero Image')}
            {renderImageCard('galleryCTAImage', 'Gallery Call To Action Image')}
          </div>
        </section>

        {/* Location Page Images Section */}
        <section className="space-y-6 pt-4">
          <div className="flex items-center gap-3 border-b border-white/10 pb-4">
            <h2 className="text-xl font-black text-white uppercase tracking-tight">Location Page</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {renderImageCard('locationHeroImage', 'Location Hero Image')}
            {renderImageCard('locationServiceAreaImage', 'Service Area Map/Image')}
          </div>
        </section>

        {/* About Us Page Images Section */}
        <section className="space-y-6 pt-4">
          <div className="flex items-center gap-3 border-b border-white/10 pb-4">
            <h2 className="text-xl font-black text-white uppercase tracking-tight">About Us Page</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {renderImageCard('aboutHeroImage', 'About Hero Image')}
            {renderImageCard('aboutStoryImage', 'Brand Story Image')}
            {renderImageCard('aboutWhy1Image', 'Why Bucara Image 1 (Authentic)')}
            {renderImageCard('aboutWhy2Image', 'Why Bucara Image 2 (Fresh)')}
            {renderImageCard('aboutWhy3Image', 'Why Bucara Image 3 (Friendly)')}
            {renderImageCard('aboutWhy4Image', 'Why Bucara Image 4 (Food Truck)')}
            <div className="md:col-span-2">
              {renderImageCard('aboutCTAImage', 'Bottom Call To Action Image')}
            </div>
          </div>
        </section>

        <div className="flex items-center gap-6 pt-4 pb-12">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#F57F25] hover:bg-[#e06d15] text-black font-black px-10 py-5 rounded-3xl transition-all flex items-center justify-center gap-3 uppercase text-sm shadow-xl shadow-[#F57F25]/20 disabled:opacity-50"
          >
            {saving ? <Loader2 className="animate-spin" size={20} /> : <><Save size={20} /> {t('admin_business_update', 'Update Settings')}</>}
          </button>
          
          {success && (
            <div className="flex items-center gap-2 text-green-500 font-bold animate-bounce">
              <Globe size={20} />
              <span className="text-xs uppercase tracking-widest">{t('admin_business_success', 'Settings saved successfully!')}</span>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
